import React, { useEffect, useState, useCallback } from "react";
import Modal from "./Modal";
import TextboxSet from "./TextboxSet";
import DropdownSet from "./DropdownSet";
import Button from "./Button";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { Clock, InfoCircle, TickCircle } from "iconsax-react";
import { salutations } from "../Constant/salutation";
import axios from "axios";
import {
  GUEST_UPDATE_API,
  GUEST_INFO_API,
  DIETARY_OPTIONS_API,
  AVAILABLE_TABLE_OPTIONS_API,
  TAG_OPTIONS_API,
} from "../Constant/api";
import { useToast } from "./Toast";

const UpdateGuestModal = ({ onClose, onSuccess, guestId }) => {
  const [loading, setLoading] = useState(false);
  const [tableList, setTableList] = useState([]);
  const [dietaryList, setDietaryList] = useState([]);
  const [tagList, setTagList] = useState([]);
  const [guestInfo, setGuestInfo] = useState(null);

  const { toast } = useToast();

  const fetchGuestInfo = useCallback(() => {
    setLoading(true);
    axios
      .get(GUEST_INFO_API, {
        headers: {
          Authorization: `Bearer ${sessionStorage.getItem("bearer_token")}`,
        },
        params: { guestId: guestId },
      })
      .then((res) => {
        setLoading(false);
        setGuestInfo(res.data.data);
      })
      .catch((err) => {
        setLoading(false);
        console.log(err);
        toast("Failed to fetch guest info.", "error");
      });
  }, [guestId, toast]);

  const fetchDietaryList = useCallback(() => {
    axios
      .get(DIETARY_OPTIONS_API, {
        headers: {
          Authorization: `Bearer ${sessionStorage.getItem("bearer_token")}`,
        },
        params: { eventId: sessionStorage.getItem("event_id") },
      })
      .then((res) => {
        setDietaryList(res.data.data);
      })
      .catch((err) => {
        console.log(err);
        toast("Failed to fetch dietary list.", "error");
      });
  }, [toast]);

  const fetchTagList = useCallback(() => {
    axios
      .get(TAG_OPTIONS_API, {
        headers: {
          Authorization: `Bearer ${sessionStorage.getItem("bearer_token")}`,
        },
        params: { eventId: sessionStorage.getItem("event_id") },
      })
      .then((res) => {
        setTagList(res.data.data);
      })
      .catch((err) => {
        console.log(err);
        toast("Failed to fetch tag list.", "error");
      });
  }, [toast]);

  const fetchTableList = useCallback(() => {
    axios
      .get(AVAILABLE_TABLE_OPTIONS_API, {
        headers: {
          Authorization: `Bearer ${sessionStorage.getItem("bearer_token")}`,
        },
        params: { eventId: sessionStorage.getItem("event_id") },
      })
      .then((res) => {
        setTableList(res.data.data);
      })
      .catch((err) => {
        console.log(err);
        toast("Failed to fetch table list.", "error");
      });
  }, [toast]);

  const handleUpdate = () => {
    if (!guestInfo.first_name || !guestInfo.last_name) {
      toast("First name and last name are required.", "error");
      return;
    }

    setLoading(true);
    axios
      .put(GUEST_UPDATE_API, guestInfo, {
        headers: {
          Authorization: `Bearer ${sessionStorage.getItem("bearer_token")}`,
        },
      })
      .then((res) => {
        setLoading(false);
        toast("Guest information updated successfully.", "success");
        handleClose();
        if (onSuccess) onSuccess();
      })
      .catch((err) => {
        setLoading(false);
        console.log(err);
        toast("Failed to update guest information.", "error");
      });
  };

  const handleOnChange = (e) => {
    setGuestInfo({ ...guestInfo, [e.target.name]: e.target.value });
  };

  const handleDropDownChange = (name, value) => {
    setGuestInfo({ ...guestInfo, [name]: value });
  };

  const handleClose = () => {
    setGuestInfo(null);
    onClose();
  };

  useEffect(() => {
    console.log(guestId);
    if (guestId) {
      fetchTableList();
      fetchDietaryList();
      fetchTagList();
      fetchGuestInfo();
    }
  }, [guestId, fetchTableList, fetchDietaryList, fetchTagList, fetchGuestInfo]);

  return (
    <Modal
      toggleModal={handleClose}
      title="Edit Guest"
      handleSubmit={handleUpdate}
      submitLabel="Save Changes"
      loading={loading}
    >
      {loading ? (
        <div className="flex items-center justify-center py-[48px] w-full">
          <p>Loading...</p>
        </div>
      ) : (
        guestInfo && (
          <div className="flex flex-col w-full gap-[16px]">
            {/* Group Name */}
            <div className="flex flex-col w-full gap-[8px]">
              <h2 className="text-[14px]">Group Name</h2>
              <div className="w-full px-[12px] py-[11px] border border-[#EFF0F2] rounded-xl">
                <input
                  type="text"
                  name="group_name"
                  value={guestInfo.group_name || ""}
                  className="w-full focus:outline-none"
                  onChange={handleOnChange}
                />
              </div>
              <p className="flex text-[#A1A7B2] text-[11px] gap-[4px] items-center">
                <InfoCircle size="11" variant="Bold" />
                Leave this field empty if he/she is individual
              </p>
            </div>

            {/* Salutation */}
            <DropdownSet
              value={guestInfo.salutation}
              options={salutations}
              label="Title/Salutation"
              name="salutation"
              onChange={handleDropDownChange}
            />

            {/* First & Last Name */}
            <div className="grid grid-cols-2 gap-[16px]">
              <TextboxSet
                type="text"
                name="first_name"
                value={guestInfo.first_name}
                label="First Name"
                required
                className="w-full"
                onChange={handleOnChange}
              />
              <TextboxSet
                type="text"
                name="last_name"
                value={guestInfo.last_name}
                label="Last Name"
                required
                className="w-full"
                onChange={handleOnChange}
              />
            </div>

            {/* Email */}
            <TextboxSet
              type="email"
              name="email"
              value={guestInfo.email}
              label="Email"
              className="w-full"
              onChange={handleOnChange}
            />

            {/* Contact Number */}
            <div className="flex flex-col w-full gap-[6px]">
              <h2>Contact Number</h2>
              <PhoneInput
                country={"my"}
                value={guestInfo.contact}
                inputStyle={{
                  width: "100%",
                  height: "38px",
                  borderRadius: "8px",
                  fontSize: "14px",
                  borderColor: "#EFF0F2",
                }}
                buttonStyle={{
                  borderRadius: "8px",
                  borderColor: "#EFF0F2",
                }}
                onChange={(phone) =>
                  setGuestInfo((prev) => {
                    let newGuestInfo = { ...prev };
                    newGuestInfo.contact = phone;
                    return newGuestInfo;
                  })
                }
              />
            </div>

            {/* Table Number */}
            <DropdownSet
              value={guestInfo.table_id}
              options={tableList}
              label="Table Number"
              name="table_id"
              onChange={handleDropDownChange}
            />

            {/* Special Dietary */}
            <DropdownSet
              value={guestInfo.dietary_ids}
              options={dietaryList}
              label="Special Dietary"
              name="dietary_ids"
              isMulti
              onChange={handleDropDownChange}
            />

            {/* Tags */}
            <DropdownSet
              value={guestInfo.tag_ids}
              options={tagList}
              label="Tags"
              name="tag_ids"
              isMulti
              onChange={handleDropDownChange}
            />

            {/* Note */}
            <TextboxSet
              type="textarea"
              name="note"
              value={guestInfo.note}
              label="Note"
              className="w-full"
              onChange={handleOnChange}
            />

            {/* Check-In Status */}
            <div className="flex flex-col w-full gap-[8px]">
              <h2>
                Check-In Status
                <span className="text-red-500">*</span>
              </h2>
              <div className="flex gap-[16px]">
                <Button
                  className="flex w-full items-center gap-[8px]"
                  variant={guestInfo.status === false ? "primary" : "secondary"}
                  onClick={() => setGuestInfo({ ...guestInfo, status: false })}
                >
                  <Clock size="16" variant="Bold" />
                  Pending
                </Button>
                <Button
                  className="flex w-full items-center gap-[8px]"
                  variant={guestInfo.status === true ? "primary" : "secondary"}
                  onClick={() => setGuestInfo({ ...guestInfo, status: true })}
                >
                  <TickCircle size="16" variant="Bold" />
                  Checked
                </Button>
              </div>
            </div>
          </div>
        )
      )}
    </Modal>
  );
};

export default UpdateGuestModal;
